import React, { useState } from 'react';
import { Card, Button } from 'react-daisyui';

const ValidatorSearch = () => {
  const [searchAddress, setSearchAddress] = useState('');
  const [validatorData, setValidatorData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const searchValidator = async () => {
    if (!searchAddress.trim()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`https://berachain-v2-blacknodes-api.blacknodes.net/validator/${searchAddress}`);
      const data = await response.json();
      
      if (response.ok) {
        setValidatorData(data);
      } else {
        setError(data.error || 'Failed to fetch validator data');
      }
    } catch (err) {
      setError('Failed to fetch validator data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search validator by address..."
            value={searchAddress}
            onChange={(e) => setSearchAddress(e.target.value)}
            className="w-full input input-bordered bg-gray-900 border-gray-300 text-white"
          />
        </div>
        <Button 
          onClick={searchValidator}
          disabled={loading || !searchAddress.trim()}
          className="btn btn-primary"
        >
          {loading ? 'Searching...' : 'Search'}
        </Button>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {validatorData && (
        <div className="space-y-6">
          <Card className="bg-gray-800 border-gray-700 shadow-[0px_20px_207px_10px_rgba(38,_126,_218,_0.93)]">
            <Card.Body>
              <div className="flex items-center gap-4 mb-6">
                {validatorData.validator.logo_uri && (
                  <img
                    src={validatorData.validator.logo_uri}
                    alt={`${validatorData.validator.name} logo`}
                    className="w-12 h-12 rounded-full bg-white object-contain"
                    onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder-logo.png'; }}
                  />
                )}
                <div>
                  <h2 className="text-2xl font-bold text-white">{validatorData.validator.name}</h2>
                  <p className="text-gray-400">{validatorData.validator.description}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold mb-2 text-gray-300">Validator Details</h3>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-400">
                      <span className="font-medium text-gray-300">Address:</span><br />
                      <span className="font-mono">{validatorData.validator.address}</span>
                    </p>
                    <p className="text-sm">
                      <span className="font-medium text-gray-300">Website:</span><br />
                      <a href={validatorData.validator.website} target="_blank" rel="noopener noreferrer" 
                         className="text-blue-400 hover:text-blue-300">
                        {validatorData.validator.website}
                      </a>
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2 text-gray-300">Performance Metrics</h3>
                  <div className="space-y-4">
                    <div className="bg-gray-900 p-4 rounded-lg">
                      <p className="text-sm mb-2">
                        <span className="font-medium text-gray-300">Uptime (24h):</span>
                        <span className="ml-2 text-green-400 text-lg font-bold">{validatorData.uptime_24h.toFixed(2)}%</span>
                      </p>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full" 
                          style={{ width: `${validatorData.uptime_24h}%` }}
                        />
                      </div>
                    </div>
                    <div className="bg-gray-900 p-4 rounded-lg">
                      <p className="text-sm">
                        <span className="font-medium text-gray-300">Missed Blocks (24h):</span>
                        <div className="mt-1">
                          <span className="text-lg font-bold text-red-400">{validatorData.missed_blocks_24h}</span>
                          <span className="text-gray-400"> / </span>
                          <span className="text-gray-300">{validatorData.total_blocks_24h}</span>
                        </div>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          <Card className="bg-gray-800 border-gray-700">
            <Card.Body>
              <h3 className="text-lg font-semibold mb-4 text-gray-300">Latest Signed Blocks (128)</h3>
              <div className="overflow-x-auto">
                <div className="flex gap-1 min-w-max pb-2">
                  {validatorData.last_128_blocks.slice(0, 128).map((block) => (
                    <div
                      key={block.height}
                      className={`w-6 h-6 rounded flex items-center justify-center text-xs shrink-0
                        ${block.signed ? 'bg-green-600 text-green-100' : 'bg-red-600 text-red-100'}`}
                      title={`Block ${block.height}: ${block.signed ? 'Signed' : 'Missed'}
Timestamp: ${new Date(block.timestamp).toLocaleString()}`}
                    >
                      {block.signed ? '✓' : '✗'}
                    </div>
                  ))}
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ValidatorSearch;